import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ButtonAuth from '../../../components/ButtonAuth';
import Input from '../../../components/Input';
import { Controller, useForm } from 'react-hook-form';
import { useTrackingContext } from '../../../context/trackingContext';
import useClient from '../../../hooks/useClient';
import Cookies from 'js-cookie';
import Alert from '../../../components/Alert';
import config from '../../../config';
import { useUserContext } from '../../../context/userContext';
import logo from '../../../assets/images/barb-logo.webp';

const StepOneLogin = () => {
  const { trackPageView, trackFormStart, trackFormSubmit, trackClick, trackError, setInternal } =
    useTrackingContext();
  const { setUserData, setUsername } = useUserContext();
  const location = useLocation();

  useEffect(() => {
    trackPageView({
      pageName: 'Login page'
    });
  }, []);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty }
  } = useForm({ mode: 'onChange' });
  const navigate = useNavigate();
  const client = useClient();
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    isDirty &&
      trackFormStart({
        formName: 'Login form start'
      });

    setUserData();
  }, [isDirty]);

  const onSubmit = async (data) => {
    const { login, password } = data;

    const params = new URLSearchParams(location.search);
    const isSupport = params.has('brand_id');

    trackFormSubmit({
      formName: 'Login form submit'
    });

    try {
      const response = await client.post(`${config.everywhereApi}/users/login`, {
        email: login,
        password,
        isSupport
      });

      const {
        accessToken,
        recognisedIP,
        verifiedEmail,
        requireNameChange,
        supportToken,
        internal
      } = response.data.data;

      if (supportToken) {
        const a = document.createElement('a');
        a.href = supportToken;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        Cookies.set('_barbToken', accessToken, {
          expires: verifiedEmail && recognisedIP ? 1 : 1 / 36
        });

        setInternal(internal);

        if (!verifiedEmail) {
          navigate('/login/verify-email');
        } else if (requireNameChange) {
          setUsername(requireNameChange);
          navigate('/login/username-change');
        } else {
          navigate('/account');
        }
      }
    } catch (err) {
      const error = err?.response?.data?.error;

      trackError({ error_text: error?.errorMessage });

      setShowAlert({
        text: error ? error?.errorMessage + ' - ' + error.errorCode : 'Unknown error',
        success: false
      });
    }
  };

  return (
    <>
      <form id="step-one" onSubmit={handleSubmit(onSubmit)}>
        <h1 className="font-Korolev-Bold text-5xl xl:text-5xl uppercase mb-5">account login</h1>
        <div className="mb-5">
          <img src={logo} alt="logo" className="w-[300px]" />
        </div>

        <div className="mb-5">
          <Controller
            control={control}
            name="login"
            defaultValue=""
            rules={{
              required: true
            }}
            render={({ field: { value, onChange } }) => (
              <Input
                type="email"
                placeholder="EMAIL"
                value={value}
                autoFocus
                onChange={onChange}
                className="w-full"
                questionIcon={false}
              />
            )}
          />
        </div>

        <div className="mb-8">
          <Controller
            control={control}
            name="password"
            defaultValue={''}
            rules={{
              required: true
            }}
            render={({ field: { value, onChange, onBlur } }) => (
              <Input
                type="password"
                placeholder="PASSWORD"
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                className="w-full tracking-widest"
                questionIcon={false}
                passwordVisiblity
              />
            )}
          />
        </div>

        <hr className="solid mb-8 text-gray-light"></hr>

        <ButtonAuth
          label={'LOG IN'}
          className="mb-5"
          type={'submit'}
          isSubmitting={isSubmitting}
          isDisabled={!isValid}
        />

        <ButtonAuth
          label={'NEED HELP LOGGING IN?'}
          type="button"
          className="mb-5"
          onClick={(e) => {
            e.preventDefault();
            trackClick({ btnName: 'Need help Logging In' });
            navigate('/login-help');
          }}
        />

        <Link
          onClick={() => {
            trackClick({ btnName: 'Create your free account' });
          }}
          to="/create-account"
          className="text-black text-center  underline inline-block mb-5">
          Create your free account
        </Link>
      </form>
      {showAlert && (
        <Alert setShowAlert={setShowAlert} success={false}>
          <p className="pr-12 text-sm md:text-base">Login failed: {showAlert.text}</p>
          {showAlert.text.includes('19643') ||
            (showAlert.text.includes('19680') && (
              <>
                <br />
                <p className="bg-black text-white inline p-2">
                  We&apos;ve enhanced our account security since the Closed Alpha. If you&apos;re
                  having trouble logging in, please reset your password.
                </p>
              </>
            ))}
        </Alert>
      )}
    </>
  );
};

export default StepOneLogin;
